import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, MessageCircle, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const MAX = 500;

/**
 * Community comments for a fact-check result.
 * Public read, anon-write (rate-limited), logged-in write with delete-my-own.
 */
export default function Comments({ checkId, initiallyOpen = true }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState(false);
    const [open, setOpen] = useState(initiallyOpen);

    useEffect(() => {
        if (!checkId) return;
        let alive = true;
        setLoading(true);
        api.get(`/checks/${checkId}/comments`).then((r) => {
            if (!alive) return;
            setItems(r.data.comments || []);
        }).finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [checkId]);

    async function submit(e) {
        e?.preventDefault?.();
        const clean = text.trim();
        if (!clean) return toast.error("Type something first");
        if (clean.length > MAX) return toast.error(`Max ${MAX} characters`);
        setPosting(true);
        try {
            const { data } = await api.post(`/checks/${checkId}/comments`, { text: clean });
            setItems((prev) => [data, ...prev]);
            setText("");
            toast.success("Comment posted");
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || "Could not post");
        } finally {
            setPosting(false);
        }
    }

    async function remove(cid) {
        if (!confirm("Delete this comment?")) return;
        try {
            await api.delete(`/checks/${checkId}/comments/${cid}`);
            setItems((prev) => prev.filter((c) => c.comment_id !== cid));
            toast.success("Comment deleted");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete");
        }
    }

    return (
        <section className="mt-8" data-testid="comments-section">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors mb-4"
                data-testid="comments-toggle"
            >
                <MessageCircle className="h-4 w-4" />
                <span className="font-medium">
                    Community comments ({items.length})
                </span>
                <span className="ml-1">{open ? "▾" : "▸"}</span>
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: "hidden" }}
                    >
                        <form onSubmit={submit} className="mb-5" data-testid="comments-form">
                            <div className="flex items-start gap-2">
                                <textarea
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    placeholder={user ? "Add your take…" : "Comment as anonymous (3/hr) or sign in for unlimited"}
                                    className="flex-1 min-h-[68px] rounded-2xl bg-white/5 border border-white/10 focus:border-primary/50 focus:outline-none px-4 py-3 text-sm text-accent placeholder:text-muted-foreground/60 resize-none"
                                    maxLength={MAX}
                                    data-testid="comments-input"
                                />
                                <Button
                                    type="submit"
                                    disabled={posting || !text.trim()}
                                    className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shrink-0"
                                    data-testid="comments-submit"
                                >
                                    {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                </Button>
                            </div>
                            <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
                                <span>{user ? `Posting as ${user.name || user.email}` : "Sign in to keep replies under your name"}</span>
                                <span className={text.length > MAX * 0.9 ? "text-amber-400" : ""}>{text.length}/{MAX}</span>
                            </div>
                        </form>

                        {loading ? (
                            <div className="text-center py-6 text-muted-foreground">
                                <Loader2 className="h-5 w-5 mx-auto animate-spin" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="text-center py-8 text-sm text-muted-foreground" data-testid="comments-empty">
                                Be the first to comment.
                            </div>
                        ) : (
                            <ul className="space-y-2.5" data-testid="comments-list">
                                {items.map((c) => (
                                    <li
                                        key={c.comment_id}
                                        className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
                                        data-testid={`comment-${c.comment_id}`}
                                    >
                                        <div className="flex items-baseline justify-between mb-1 gap-2">
                                            <span className="text-xs font-semibold text-accent">
                                                {c.author_name}
                                            </span>
                                            <span className="text-[10px] font-mono text-muted-foreground">
                                                {new Date(c.created_at).toLocaleString()}
                                            </span>
                                        </div>
                                        <p className="text-sm text-accent/90 whitespace-pre-wrap break-words">
                                            {c.text}
                                        </p>
                                        {c.is_owner && (
                                            <button
                                                onClick={() => remove(c.comment_id)}
                                                className="mt-2 text-[11px] text-red-300 hover:text-red-200 flex items-center gap-1"
                                                data-testid={`comment-delete-${c.comment_id}`}
                                            >
                                                <Trash2 className="h-3 w-3" /> Delete
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
